import type { ParsedReportCsv } from "./parseReportCsv";
import { formatReportGeneratedLabel } from "./reportDateRanges";

// Report Preview Sheets (24) printEventAttendees (:3084-3115), CLAUDE.md
// "Printing the event Attendee Report" (IF11 EV14, Monday 13400145507): the
// Event Attendee List prints only the event set-up columns, from Manage
// Events and from the Reports page. Never Invoice #, Event or Ticket Price;
// Special Accommodations and Submitted Question only when the report has
// them. Rows of more than one event print under an event heading row.

type SeatCell = { text: string; detail?: string | null };

const escapeHtml = (value: string | null | undefined) =>
  String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const buildEventAttendeePrintHtml = ({
  csv,
  eventDates = [],
  eventLabel,
  filters,
  generatedLabel,
  invoiceCells,
  periodLabel,
}: {
  csv: ParsedReportCsv;
  /** Each event's date from the report (S21), for the event heading rows. */
  eventDates?: ReadonlyArray<{ event: string; date: string }> | null;
  eventLabel: string;
  filters: ReadonlyArray<{ label: string; value: string }>;
  generatedLabel: string;
  /** The sheet's Invoice # cells in CSV row order; a table seat has "1 of 8". */
  invoiceCells: ReadonlyArray<SeatCell | undefined>;
  periodLabel: string;
}): string => {
  const column = (name: string) => csv.columns.indexOf(name);
  const value = (row: string[], name: string) => (column(name) > -1 ? row[column(name)] || "" : "");
  const seatLabel = (row: string[], index: number) => {
    const seat = /^(\d+) of \d+$/.exec(invoiceCells[index]?.detail || "");
    return seat ? `${seat[1]} of ${value(row, "Ticket Type")}` : value(row, "Ticket Type");
  };
  const columns: Array<[string, (row: string[], index: number) => string]> = [
    ["Ticket Type", (row, index) => escapeHtml(seatLabel(row, index))],
    ["Attendee Details", (row) =>
      `<div style="font-weight:600;">${escapeHtml(value(row, "Attendee"))}</div>` +
      `<div style="color:#555;">${escapeHtml(value(row, "Company"))}</div>` +
      `<div style="color:#555;">${escapeHtml(value(row, "Email"))}</div>`],
    ["Dietary Options", (row) => escapeHtml(value(row, "Dietary Options") || "No Selection")],
  ];
  if (column("Special Accommodations") > -1) {
    columns.push(["Special Accommodations", (row) => escapeHtml(value(row, "Special Accommodations") || "No Selection")]);
  }
  if (column("Submitted Question") > -1) {
    columns.push(["Submitted Question", (row) => escapeHtml(value(row, "Submitted Question"))]);
  }
  columns.push(["Additional Details", (row) => escapeHtml(value(row, "Additional Details"))]);
  const head = columns.map(([label]) => `<th>${label}</th>`).join("");
  const rowHtml = (row: string[], index: number) =>
    `<tr>${columns.map(([, render]) => `<td>${render(row, index)}</td>`).join("")}</tr>`;
  const indexedRows = csv.rows.map((row, index) => ({ row, index }));
  const events = Array.from(new Set(csv.rows.map((row) => value(row, "Event")).filter(Boolean)));
  // The heading reads "<event> — <event date>": the report's eventDates
  // (Monday 13400145507), else an Event Date column when a report has one.
  const eventHeading = (event: string) => {
    const dateRow = csv.rows.find((row) => value(row, "Event") === event && value(row, "Event Date"));
    const date = (eventDates || []).find((item) => item.event === event)?.date || (dateRow ? value(dateRow, "Event Date") : "");
    return escapeHtml(event) + (date ? ` \u2014 ${escapeHtml(date)}` : "");
  };
  const body = events.length > 1
    ? events.map((event) =>
      `<tr class="ev"><td colspan="${columns.length}">${eventHeading(event)}</td></tr>` +
      indexedRows.filter(({ row }) => value(row, "Event") === event).map(({ row, index }) => rowHtml(row, index)).join("")).join("")
    : indexedRows.map(({ row, index }) => rowHtml(row, index)).join("");
  const filterText = filters.map(({ label, value: filterValue }) => `${escapeHtml(label)}: ${escapeHtml(filterValue)}`).join("<br>");
  return "<!doctype html><html><head><title>Event Attendee List</title>" +
    '<link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&display=swap" rel="stylesheet">' +
    '<style>@page{size:landscape;margin:12mm}body{font-family:"DM Sans",sans-serif;color:#0A0A0A;margin:0;font-size:12px}' +
    "h1{font-size:20px;margin:0 0 4px}.meta{display:flex;justify-content:space-between;gap:24px;margin:0 0 14px;font-size:12px}.meta b{font-weight:600}" +
    "table{width:100%;border-collapse:collapse}th{text-align:left;font-size:11px;font-weight:700;background:#F7F7F8;border-bottom:1.5px solid #0A0A0A;padding:8px 6px}" +
    "td{vertical-align:top;border-bottom:1px solid #DDD;padding:7px 6px;line-height:1.4}tr{page-break-inside:avoid}thead{display:table-header-group}" +
    "tr.ev td{font-weight:700;font-size:13px;background:#F7F7F8;border-bottom:1.5px solid #0A0A0A;padding:10px 6px 6px}" +
    ".foot{margin-top:10px;font-size:11px;color:#555}</style></head><body>" +
    "<h1>Event Attendee List</h1>" +
    `<div class="meta"><div><b>${escapeHtml(eventLabel)}</b><br>${escapeHtml(periodLabel)}</div>` +
    `<div style="text-align:right;">Generated ${escapeHtml(generatedLabel)}${filterText ? `<br>${filterText}` : ""}</div></div>` +
    `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>` +
    `<div class="foot">${csv.rows.length} attendees</div></body></html>`;
};

type PreviewCell = { text: string; detail?: string | null };

/**
 * Prints a loaded Event Attendee List preview with the design print. The
 * preview sheet's Invoice # cells give the table seat labels ("1 of 8").
 * Returns false when the browser blocked the print window.
 */
export const printEventAttendeePreview = ({
  eventLabel,
  filters,
  periodLabel,
  preview,
  timeZone,
}: {
  eventLabel: string;
  filters: ReadonlyArray<{ label: string; value: string }>;
  periodLabel: string;
  preview: ParsedReportCsv & {
    document?: {
      columns: readonly string[];
      rows: ReadonlyArray<{ cells: ReadonlyArray<PreviewCell> }>;
      eventDates?: ReadonlyArray<{ event: string; date: string }> | null;
    } | null;
    generatedAtUtcMilli: number;
  };
  timeZone?: string | null;
}): boolean => {
  const invoiceColumn = preview.document?.columns.indexOf("Invoice #") ?? -1;
  return printEventAttendeeReport(buildEventAttendeePrintHtml({
    csv: preview,
    eventDates: preview.document?.eventDates,
    eventLabel,
    filters,
    generatedLabel: formatReportGeneratedLabel({ generatedAtUtcMilli: preview.generatedAtUtcMilli, timeZone }),
    invoiceCells: (preview.document?.rows || []).map((row) => (invoiceColumn > -1 ? row.cells[invoiceColumn] : undefined)),
    periodLabel,
  }));
};

/** Opens the print view in a new window, as the design does. */
export const printEventAttendeeReport = (html: string): boolean => {
  const printWindow = window.open("", "_blank");
  if (!printWindow) return false;
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  window.setTimeout(() => {
    try {
      printWindow.print();
    } catch {
      // The window was closed before printing.
    }
  }, 500);
  return true;
};
